/** The distinct values of one field of a data model: what kind of field it is, how common each value is, searchable and sortable. */
import { useMemo } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Api } from '../lib/api.ts';
import { useAsync } from '../lib/hooks.ts';
import { count, ago } from '../lib/format.ts';
import { filterValues, shareOf, sortValues, summarize, SORT_LABEL, VALUE_PAGE, VALUE_SORTS, type ValueSort } from '../lib/distinct.ts';
import { highlightParts } from '../lib/highlight.ts';
import { cx, IconButton, Menu } from '../design/primitives.tsx';
import { CloseIcon, CopyIcon, FilterIcon, SearchIcon } from './icons.tsx';
import { Button, Spinner, useToast } from './ui.tsx';

export interface DistinctView { vs: ValueSort; vq: string; vn: number }

export function DistinctValues({ tid, cls, path, fieldCount, view, onView, onQuery, onClose, onSearch, onActual }: {
  tid: string; cls: string; path: string;
  /** Documents that have the field, to tell whether the newest ones checked were all of them. */
  fieldCount: number;
  view: DistinctView;
  onView: (patch: Partial<DistinctView>) => void;
  /** Text typed in the filter (kept in the URL by the caller). */
  onQuery: (text: string) => void;
  onClose: () => void;
  /** Show actual documents with this value of the field in the data model view (stays in Settings). */
  onActual: (value: string) => void;
  /** Open the data search for a value of the field (leaves Settings). */
  onSearch: (value: string) => void;
}) {
  const toast = useToast();
  const found = useAsync(() => Api.fieldValues(tid, cls, path), [tid, cls, path]);
  const all = found.data?.values ?? [];
  const summary = useMemo(() => summarize(all), [all]);
  const shown = useMemo(() => sortValues(filterValues(all, view.vq), view.vs, summary.kind), [all, view.vq, view.vs, summary.kind]);
  const complete = found.data ? fieldCount <= found.data.limit : false;
  const visible = shown.slice(0, view.vn);
  const copy = (text: string, what: string) => { void navigator.clipboard?.writeText(text); toast(`${what} copied`); };

  return (
    <Dialog.Root open onOpenChange={(open) => { if (!open) onClose(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40 backdrop-blur-[2px]" />
        <Dialog.Content aria-describedby={undefined}
          className="fixed z-50 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(960px,94vw)] max-h-[88vh] flex flex-col gap-3 bg-n2 text-n10 rounded-card shadow-float p-5 outline-none mv-pop">
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0 flex flex-col gap-0.5">
          <Dialog.Title className="m-0 text-body font-semibold">Values of <span className="font-mono">{path}</span></Dialog.Title>
          <span className="text-micro text-n7 font-mono">{cls}</span>
          {found.data && (
            <span className="text-small text-n8 mt-1">
              {count(summary.distinct)} distinct value{summary.distinct === 1 ? '' : 's'} · {complete ? 'in all documents that have this field' : `from the newest ${count(found.data.limit)} documents that have this field`}
            </span>
          )}
          {found.data && summary.distinct > 0 && <span className="text-small text-n9">{summary.insight}</span>}
        </div>
        <Dialog.Close asChild><IconButton label="Close the values"><CloseIcon size={16} /></IconButton></Dialog.Close>
      </div>

      {found.loading && !found.data && <div className="py-4 grid place-items-center"><Spinner /></div>}
      {found.error && <p className="m-0 text-small text-fail">{found.error}</p>}
      {found.data && found.data.vaultErrors.length > 0 && <p className="m-0 text-small text-warn">Not all Vaults answered: {found.data.vaultErrors.map((e) => e.vaultName).join(', ')}.</p>}
      {found.data && summary.distinct === 0 && <p className="m-0 text-small text-n8">No values: the field is empty in the documents found.</p>}

      {summary.distinct > 0 && (
        <>
          <div className="flex items-center gap-3 flex-wrap">
            {summary.distinct > 6 && (
              <label className="flex items-center gap-2 h-9 px-3 rounded-chip bg-n3 text-small text-n8 w-64">
                <FilterIcon size={14} /><span className="sr-only">Filter the values</span>
                <input className="flex-1 min-w-0 bg-transparent border-0 outline-none text-n10 placeholder:text-n7" placeholder="Find a value" value={view.vq} onChange={(e) => onQuery(e.target.value)} />
              </label>
            )}
            <div role="group" aria-label="Sort the values" className="flex items-center gap-1">
              {VALUE_SORTS.map((s) => (
                <button key={s} type="button" aria-pressed={view.vs === s} onClick={() => onView({ vs: s, vn: VALUE_PAGE })}
                  className={cx('h-8 px-3 rounded-full border-0 cursor-pointer text-small', view.vs === s ? 'bg-accent-soft text-accent font-medium' : 'bg-transparent text-n8 hover:bg-n3')}>
                  {SORT_LABEL[s](summary.kind)}
                </button>
              ))}
            </div>
            <span className="flex-1" />
            <Button variant="ghost" onClick={() => copy(shown.map((v) => v.value).join('\n'), `${count(shown.length)} value${shown.length === 1 ? '' : 's'}`)}><CopyIcon size={14} />Copy {view.vq ? 'these' : 'all'}</Button>
          </div>

          <ul className="m-0 p-0 list-none flex flex-col overflow-y-auto min-h-0 flex-1">
            {visible.map((v) => {
              const share = shareOf(v, summary.total);
              return (
                <li key={v.value} className="group grid grid-cols-[minmax(0,1fr)_minmax(80px,160px)_64px_72px_32px] items-center gap-x-4 min-h-9 px-2 rounded-chip hover:bg-n3/70">
                  <span className="font-mono text-small text-n10 [overflow-wrap:anywhere] py-1">
                    {highlightParts(v.value, view.vq).map((p, i) => (p.hit ? <mark key={i} className="rounded-[3px] px-0.5" style={{ background: '#FFE14D', color: '#1B1B1B' }}>{p.text}</mark> : p.text))}
                  </span>
                  <span className="h-1.5 rounded-full bg-n4 overflow-hidden" aria-hidden="true">
                    <span className="block h-full rounded-full bg-accent" style={{ width: `${Math.max(2, Math.round(share * 100))}%` }} />
                  </span>
                  <span className="text-right font-mono text-small tabular-nums text-n9" title={`${(share * 100).toFixed(share < 0.01 ? 2 : 1)} % of the values seen`}>{count(v.count)}</span>
                  <span className="text-right text-micro text-n7 whitespace-nowrap" title="Last seen">{ago(v.last)}</span>
                  <Menu.Root>
                    <Menu.Trigger aria-label={`Actions for ${v.value}`}
                      className="size-7 rounded-full grid place-items-center bg-transparent text-n8 border-0 cursor-pointer text-body leading-none hover:bg-n4 focus-visible:ring-2 focus-visible:ring-accent">
                      <span aria-hidden="true">⋯</span>
                    </Menu.Trigger>
                    <Menu.Content align="end" className="min-w-64">
                      <Menu.Item icon={<SearchIcon size={16} />} onSelect={() => onActual(v.value)}>Show actual data with this value</Menu.Item>
                      <Menu.Item onSelect={() => copy(v.value, 'The value')}>Copy the value</Menu.Item>
                      <Menu.Separator />
                      <Menu.Label>Leaves Settings</Menu.Label>
                      <Menu.Item icon={<SearchIcon size={16} />} onSelect={() => onSearch(v.value)}>Search data with this value</Menu.Item>
                    </Menu.Content>
                  </Menu.Root>
                </li>
              );
            })}
          </ul>
          {shown.length === 0 && <p className="m-0 text-small text-n8">No value matches “{view.vq}”.</p>}
          {shown.length > visible.length && (
            <div className="flex justify-center"><Button onClick={() => onView({ vn: view.vn + VALUE_PAGE })}>Show {count(Math.min(VALUE_PAGE, shown.length - visible.length))} more of {count(shown.length - visible.length)}</Button></div>
          )}
        </>
      )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
